/**
 * Public athlete showcase — browse + individual profile + contact scout form.
 * Handles routes: /athletes, /athletes/submit, /athletes/{slug}
 */
import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import Header from "./Header";
import Footer from "./Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SPORTS = [
    "Football", "Basketball", "Baseball", "Softball", "Soccer", "Track & Field",
    "Wrestling", "Volleyball", "Lacrosse", "Golf", "Tennis", "Swimming",
    "Cross Country", "MMA", "Boxing", "Hockey", "Rugby", "Gymnastics", "Cheer", "Other",
];

const STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY"];

// ================ BROWSE PAGE =================
function FeaturedBanner() {
    const [featured, setFeatured] = useState(null);
    useEffect(() => {
        fetch(`${API}/athletes/featured/current`).then((r) => r.json()).then((d) => setFeatured(d.athlete)).catch(() => {});
    }, []);
    if (!featured) return null;
    return (
        <Link to={`/athletes/${featured.slug}`} data-testid="featured-athlete-banner" className="block border-b border-white/10 bg-gradient-to-r from-red-950/40 via-black to-black hover:from-red-900/50 transition-colors">
            <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 flex items-center gap-5">
                <div className="w-16 h-16 md:w-20 md:h-20 bg-zinc-900 overflow-hidden ring-2 ring-red-500 flex-shrink-0">
                    {featured.photo_url ? <img src={featured.photo_url} alt={featured.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-white/20 font-display text-3xl">{featured.name[0]}</div>}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="text-red-500 text-[10px] tracking-[0.4em] font-display uppercase mb-1">⭐ Athlete of the Month</div>
                    <div className="text-white font-display text-lg md:text-xl uppercase truncate">{featured.name}</div>
                    <div className="text-white/50 text-xs tracking-widest uppercase font-display truncate">{featured.sport} · {featured.school || featured.state}</div>
                </div>
                <div className="text-white/60 text-xs tracking-[0.3em] uppercase font-display hidden md:block">View Profile →</div>
            </div>
        </Link>
    );
}

export function AthletesBrowse() {
    const [athletes, setAthletes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ sport: "", class_year: "", state: "" });

    useEffect(() => {
        const q = new URLSearchParams();
        if (filters.sport) q.set("sport", filters.sport);
        if (filters.class_year) q.set("class_year", filters.class_year);
        if (filters.state) q.set("state", filters.state);
        setLoading(true);
        fetch(`${API}/athletes?${q.toString()}`)
            .then((r) => r.json())
            .then((d) => setAthletes(d.athletes || []))
            .catch(() => setAthletes([]))
            .finally(() => setLoading(false));
    }, [filters]);

    return (
        <div className="App grain min-h-screen bg-black text-white" data-testid="athletes-browse">
            <Header />
            <main className="pt-24 pb-24">
                <FeaturedBanner />
                <section className="border-b border-white/10">
                    <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-20">
                        <div className="text-red-500 text-xs tracking-[0.35em] font-display uppercase mb-3">FGM Athletes</div>
                        <h1 className="font-display text-5xl md:text-7xl uppercase text-white leading-[0.9] tracking-tight">
                            <span className="block">The Movement</span>
                            <span className="block text-red-500">Is Watching.</span>
                        </h1>
                        <p className="mt-6 max-w-2xl text-zinc-300 text-base md:text-lg">
                            Real athletes. Real highlights. Built for coaches, scouts, and anyone looking for the next generation of talent — powered by FGM Apparel.
                        </p>
                        <div className="mt-8 flex flex-wrap gap-4">
                            <Link to="/athletes/submit" data-testid="athletes-submit-cta" className="bg-red-500 hover:bg-red-600 text-white px-7 py-3.5 font-display uppercase tracking-[0.2em] text-sm">
                                Submit Your Profile →
                            </Link>
                            <Link to="/ambassador" className="text-white/70 hover:text-white text-xs tracking-[0.35em] font-display uppercase border-b border-white/20 hover:border-white pb-1">Become An Ambassador</Link>
                        </div>
                    </div>
                </section>

                {/* Filters */}
                <section className="border-b border-white/10 bg-zinc-950">
                    <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 grid grid-cols-1 md:grid-cols-4 gap-3">
                        <select value={filters.sport} onChange={(e) => setFilters((f) => ({ ...f, sport: e.target.value }))} data-testid="athletes-filter-sport" className="bg-black border border-white/20 text-white px-3 py-3 text-sm font-body">
                            <option value="">All Sports</option>
                            {SPORTS.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <select value={filters.class_year} onChange={(e) => setFilters((f) => ({ ...f, class_year: e.target.value }))} data-testid="athletes-filter-year" className="bg-black border border-white/20 text-white px-3 py-3 text-sm font-body">
                            <option value="">All Class Years</option>
                            {[2025, 2026, 2027, 2028, 2029, 2030, 2031].map((y) => <option key={y} value={y}>Class of {y}</option>)}
                        </select>
                        <select value={filters.state} onChange={(e) => setFilters((f) => ({ ...f, state: e.target.value }))} data-testid="athletes-filter-state" className="bg-black border border-white/20 text-white px-3 py-3 text-sm font-body">
                            <option value="">All States</option>
                            {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                        <button onClick={() => setFilters({ sport: "", class_year: "", state: "" })} className="text-white/60 hover:text-white text-xs tracking-[0.35em] font-display uppercase border border-white/20 py-3">
                            Clear
                        </button>
                    </div>
                </section>

                {/* Grid */}
                <section>
                    <div className="max-w-7xl mx-auto px-6 md:px-10 py-16">
                        {loading ? (
                            <div className="text-center text-white/40 font-display tracking-widest uppercase">Loading…</div>
                        ) : athletes.length === 0 ? (
                            <div className="text-center py-16">
                                <div className="text-white/60 font-body text-lg mb-4">No athletes yet in this filter.</div>
                                <Link to="/athletes/submit" className="text-red-500 hover:text-red-400 border-b border-red-500/40 text-sm uppercase tracking-[0.3em] font-display">Be the first — submit your profile</Link>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="athletes-grid">
                                {athletes.map((a) => (
                                    <Link key={a.id} to={`/athletes/${a.slug}`} data-testid={`athlete-card-${a.slug}`} className="group border border-white/10 hover:border-red-500 bg-zinc-950 transition-all overflow-hidden">
                                        <div className="aspect-[4/5] bg-zinc-900 overflow-hidden">
                                            {a.photo_url ? (
                                                <img src={a.photo_url} alt={a.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                            ) : (
                                                <div className="w-full h-full flex items-center justify-center text-6xl text-white/10 font-display">{a.name?.[0] || "A"}</div>
                                            )}
                                        </div>
                                        <div className="p-5">
                                            <div className="flex items-center gap-2 mb-1">
                                                <h3 className="text-white text-lg font-display uppercase tracking-tight">{a.name}</h3>
                                                {a.is_ambassador_verified && <span className="text-[9px] tracking-widest font-display uppercase text-red-500 border border-red-500/40 px-1.5 py-0.5">FGM ✓</span>}
                                            </div>
                                            <div className="text-white/60 text-xs tracking-[0.2em] uppercase font-display">
                                                {a.sport}{a.position ? ` · ${a.position}` : ""}{a.class_year ? ` · Class ${a.class_year}` : ""}
                                            </div>
                                            <div className="text-white/40 text-xs mt-2 font-body">
                                                {[a.school, a.state].filter(Boolean).join(" · ")}
                                            </div>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        )}
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
}

// ================ INDIVIDUAL PROFILE ==============
function embedUrl(h) {
    const url = h.url || "";
    if (h.kind === "youtube") {
        const m = url.match(/(?:v=|youtu\.be\/|embed\/)([\w-]{6,})/);
        if (m) return `https://www.youtube.com/embed/${m[1]}`;
    }
    if (h.kind === "vimeo") {
        const m = url.match(/vimeo\.com\/(\d+)/);
        if (m) return `https://player.vimeo.com/video/${m[1]}`;
    }
    return null;
}

export function AthleteProfile() {
    const { slug } = useParams();
    const [a, setA] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [showContact, setShowContact] = useState(false);
    const [showCoachVerify, setShowCoachVerify] = useState(false);
    const [contact, setContact] = useState({ scout_name: "", scout_email: "", scout_org: "", message: "" });
    const [coach, setCoach] = useState({ coach_name: "", coach_email: "", coach_title: "", coach_school: "", message: "" });
    const [sending, setSending] = useState(false);
    const [scoutToken, setScoutToken] = useState(() => localStorage.getItem("fgm_scout_token") || "");
    const [inShortlist, setInShortlist] = useState(false);

    useEffect(() => {
        fetch(`${API}/athletes/${slug}`)
            .then((r) => (r.ok ? r.json() : Promise.reject()))
            .then(setA)
            .catch(() => setNotFound(true));
    }, [slug]);

    useEffect(() => {
        if (!scoutToken) return;
        fetch(`${API}/scouts/shortlist`, { headers: { "X-Scout-Token": scoutToken } })
            .then((r) => (r.ok ? r.json() : Promise.reject()))
            .then((d) => setInShortlist((d.shortlist || []).some((i) => i.athlete?.slug === slug)))
            .catch(() => {});
    }, [scoutToken, slug]);

    async function submitContact(e) {
        e.preventDefault();
        setSending(true);
        try {
            const r = await fetch(`${API}/athletes/${slug}/contact`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(contact),
            });
            if (!r.ok) throw new Error("Failed");
            toast.success("Message delivered. The athlete will reach out.");
            setShowContact(false);
            setContact({ scout_name: "", scout_email: "", scout_org: "", message: "" });
        } catch {
            toast.error("Could not send message");
        } finally {
            setSending(false);
        }
    }

    async function submitCoachVerify(e) {
        e.preventDefault();
        setSending(true);
        try {
            const r = await fetch(`${API}/athletes/${slug}/coach-verify`, {
                method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(coach),
            });
            if (!r.ok) throw new Error("Failed");
            toast.success("Thanks coach. FGM will review and verify.");
            setShowCoachVerify(false);
            setCoach({ coach_name: "", coach_email: "", coach_title: "", coach_school: "", message: "" });
        } catch {
            toast.error("Could not submit verification");
        } finally { setSending(false); }
    }

    async function toggleShortlist() {
        if (!scoutToken) {
            toast.error("Sign in as a scout to save athletes.");
            return;
        }
        try {
            if (inShortlist) {
                await fetch(`${API}/scouts/shortlist/${slug}`, { method: "DELETE", headers: { "X-Scout-Token": scoutToken } });
                setInShortlist(false);
                toast.success("Removed from shortlist");
            } else {
                await fetch(`${API}/scouts/shortlist`, { method: "POST", headers: { "Content-Type": "application/json", "X-Scout-Token": scoutToken }, body: JSON.stringify({ athlete_slug: slug }) });
                setInShortlist(true);
                toast.success("Saved to shortlist");
            }
        } catch { toast.error("Shortlist action failed"); }
    }

    if (notFound) {
        return (
            <div className="App grain min-h-screen bg-black text-white">
                <Header />
                <main className="pt-32 max-w-3xl mx-auto px-6 text-center">
                    <h1 className="font-display text-4xl uppercase text-white">Athlete Not Found</h1>
                    <p className="mt-4 text-white/60"><Link to="/athletes" className="text-red-500 border-b border-red-500/40">Back to browse</Link></p>
                </main>
                <Footer />
            </div>
        );
    }
    if (!a) return null;

    return (
        <div className="App grain min-h-screen bg-black text-white" data-testid="athlete-profile">
            <Header />
            <main className="pt-24 pb-24">
                <section className="border-b border-white/10">
                    <div className="max-w-6xl mx-auto px-6 md:px-10 py-12 grid grid-cols-1 md:grid-cols-2 gap-10">
                        <div className="aspect-[4/5] bg-zinc-900 overflow-hidden ring-1 ring-white/10">
                            {a.photo_url ? <img src={a.photo_url} alt={a.name} className="w-full h-full object-cover" /> : (
                                <div className="w-full h-full flex items-center justify-center text-8xl text-white/10 font-display">{a.name?.[0]}</div>
                            )}
                        </div>
                        <div>
                            <div className="flex items-center gap-2 mb-2">
                                <div className="text-red-500 text-xs tracking-[0.35em] font-display uppercase">FGM Athlete</div>
                                {a.is_ambassador_verified && <span className="text-[10px] tracking-widest font-display uppercase text-red-500 border border-red-500/40 px-2 py-0.5">FGM ✓ Verified</span>}
                            </div>
                            <h1 className="font-display text-4xl md:text-5xl uppercase text-white leading-tight tracking-tight">{a.name}</h1>
                            <div className="mt-3 text-white/70 text-sm tracking-[0.25em] font-display uppercase">
                                {a.sport}{a.position ? ` · ${a.position}` : ""}{a.class_year ? ` · Class of ${a.class_year}` : ""}
                            </div>
                            <div className="mt-2 text-white/50 text-sm font-body">
                                {[a.school, a.city, a.state].filter(Boolean).join(" · ")}
                            </div>

                            {(a.height || a.weight || a.gpa) && (
                                <div className="mt-6 grid grid-cols-3 gap-3 max-w-md">
                                    {a.height && <div className="border border-white/10 p-3 text-center"><div className="text-xs text-white/40 tracking-widest uppercase font-display">Height</div><div className="text-white font-display text-lg mt-1">{a.height}</div></div>}
                                    {a.weight && <div className="border border-white/10 p-3 text-center"><div className="text-xs text-white/40 tracking-widest uppercase font-display">Weight</div><div className="text-white font-display text-lg mt-1">{a.weight} lb</div></div>}
                                    {a.gpa && <div className="border border-white/10 p-3 text-center"><div className="text-xs text-white/40 tracking-widest uppercase font-display">GPA</div><div className="text-white font-display text-lg mt-1">{a.gpa}</div></div>}
                                </div>
                            )}

                            {a.achievements?.length > 0 && (
                                <div className="mt-6">
                                    <div className="text-white/50 text-xs tracking-[0.3em] font-display uppercase mb-3">Achievements</div>
                                    <ul className="space-y-1.5">
                                        {a.achievements.map((x, i) => <li key={i} className="text-white/80 text-sm flex gap-2"><span className="text-red-500">▸</span>{x}</li>)}
                                    </ul>
                                </div>
                            )}

                            <div className="mt-8 flex flex-wrap gap-3">
                                <button onClick={() => setShowContact(true)} data-testid="athlete-contact-btn" className="bg-red-500 hover:bg-red-600 text-white px-6 py-3 font-display uppercase tracking-[0.25em] text-sm">Contact This Athlete</button>
                                <button onClick={toggleShortlist} data-testid="athlete-shortlist-btn" className={`border px-4 py-3 text-xs tracking-[0.3em] uppercase font-display transition-colors ${inShortlist ? "bg-red-500/20 border-red-500 text-red-400" : "border-white/20 hover:border-white text-white/70 hover:text-white"}`}>{inShortlist ? "★ On Shortlist" : "☆ Save"}</button>
                                <button onClick={() => setShowCoachVerify(true)} data-testid="athlete-coach-verify-btn" className="border border-white/20 hover:border-white text-white/70 hover:text-white px-4 py-3 text-xs tracking-[0.3em] uppercase font-display">Coach? Verify</button>
                                {a.instagram && <a href={`https://instagram.com/${a.instagram.replace("@","")}`} target="_blank" rel="noreferrer" className="border border-white/20 hover:border-white text-white/70 hover:text-white px-4 py-3 text-xs tracking-[0.3em] uppercase font-display">Instagram</a>}
                                {a.tiktok && <a href={`https://tiktok.com/@${a.tiktok.replace("@","")}`} target="_blank" rel="noreferrer" className="border border-white/20 hover:border-white text-white/70 hover:text-white px-4 py-3 text-xs tracking-[0.3em] uppercase font-display">TikTok</a>}
                            </div>

                            {a.coach_verifications_public?.length > 0 && (
                                <div className="mt-6 border-t border-white/10 pt-6">
                                    <div className="text-red-500 text-[10px] tracking-[0.35em] font-display uppercase mb-3">Verified By Coaches ({a.coach_verified_count})</div>
                                    <div className="space-y-2">
                                        {a.coach_verifications_public.map((c, i) => (
                                            <div key={i} className="text-white/70 text-sm">
                                                <span className="text-white">{c.coach_name}</span>
                                                {c.coach_title ? <span className="text-white/50"> · {c.coach_title}</span> : null}
                                                <span className="text-white/50"> · {c.coach_school}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </section>

                {a.highlights?.length > 0 && (
                    <section className="border-b border-white/10">
                        <div className="max-w-6xl mx-auto px-6 md:px-10 py-12">
                            <div className="text-red-500 text-xs tracking-[0.35em] font-display uppercase mb-2">Highlights</div>
                            <h2 className="font-display text-3xl uppercase text-white mb-8">Watch The Grind</h2>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {a.highlights.map((h, i) => {
                                    const embed = embedUrl(h);
                                    return (
                                        <div key={i} className="border border-white/10 bg-zinc-950">
                                            {embed ? (
                                                <div className="aspect-video"><iframe src={embed} title={h.title || `Highlight ${i+1}`} className="w-full h-full" allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowFullScreen /></div>
                                            ) : (
                                                <a href={h.url} target="_blank" rel="noreferrer" className="block aspect-video flex items-center justify-center bg-zinc-900 hover:bg-zinc-800 transition-colors p-6 text-center">
                                                    <div>
                                                        <div className="text-white/60 text-xs tracking-widest uppercase font-display mb-2">{h.kind}</div>
                                                        <div className="text-white text-sm font-body">{h.title || "Watch on external site →"}</div>
                                                    </div>
                                                </a>
                                            )}
                                            {h.title && <div className="p-3 text-white/70 text-sm font-body">{h.title}</div>}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </section>
                )}

                <section>
                    <div className="max-w-3xl mx-auto px-6 md:px-10 py-12 text-center text-white/40 text-xs tracking-widest uppercase font-display">
                        FGM Apparel does not represent this athlete. All content is athlete-submitted.
                    </div>
                </section>
            </main>

            {showContact && (
                <div className="fixed inset-0 z-[70] bg-black/80 backdrop-blur flex items-center justify-center p-4" onClick={() => setShowContact(false)}>
                    <form onSubmit={submitContact} onClick={(e) => e.stopPropagation()} data-testid="athlete-contact-form" className="w-full max-w-md bg-zinc-950 border border-red-500/40 p-6">
                        <div className="text-red-500 text-xs tracking-[0.35em] font-display uppercase mb-2">Scout Contact</div>
                        <h3 className="font-display text-white text-2xl uppercase mb-4">Reach {a.name}</h3>
                        <p className="text-white/60 text-sm mb-6">Your message is routed through FGM and forwarded to the athlete. No cold emails.</p>
                        <input required placeholder="Your name" value={contact.scout_name} onChange={(e) => setContact({...contact, scout_name: e.target.value})} className="w-full bg-black border border-white/20 focus:border-red-500 outline-none px-4 py-3 text-white text-sm mb-3" />
                        <input required type="email" placeholder="Your email" value={contact.scout_email} onChange={(e) => setContact({...contact, scout_email: e.target.value})} className="w-full bg-black border border-white/20 focus:border-red-500 outline-none px-4 py-3 text-white text-sm mb-3" />
                        <input placeholder="School / Organization (optional)" value={contact.scout_org} onChange={(e) => setContact({...contact, scout_org: e.target.value})} className="w-full bg-black border border-white/20 focus:border-red-500 outline-none px-4 py-3 text-white text-sm mb-3" />
                        <textarea required rows="5" minLength={10} placeholder="Your message (min 10 chars)" value={contact.message} onChange={(e) => setContact({...contact, message: e.target.value})} className="w-full bg-black border border-white/20 focus:border-red-500 outline-none px-4 py-3 text-white text-sm mb-4" />
                        <div className="flex gap-3">
                            <button type="button" onClick={() => setShowContact(false)} className="flex-1 border border-white/20 text-white/70 py-3 text-xs tracking-[0.3em] uppercase font-display">Cancel</button>
                            <button type="submit" disabled={sending} className="flex-1 bg-red-500 hover:bg-red-600 text-white py-3 text-xs tracking-[0.3em] uppercase font-display disabled:opacity-50">{sending ? "Sending…" : "Send Message"}</button>
                        </div>
                    </form>
                </div>
            )}

            {showCoachVerify && (
                <div className="fixed inset-0 z-[70] bg-black/80 backdrop-blur flex items-center justify-center p-4" onClick={() => setShowCoachVerify(false)}>
                    <form onSubmit={submitCoachVerify} onClick={(e) => e.stopPropagation()} data-testid="coach-verify-form" className="w-full max-w-md bg-zinc-950 border border-red-500/40 p-6">
                        <div className="text-red-500 text-xs tracking-[0.35em] font-display uppercase mb-2">Coach Verification</div>
                        <h3 className="font-display text-white text-2xl uppercase mb-4">Verify {a.name}</h3>
                        <p className="text-white/60 text-sm mb-6">Coach or trainer? Add your endorsement. FGM will review before it goes public.</p>
                        <input required placeholder="Your name" value={coach.coach_name} onChange={(e) => setCoach({...coach, coach_name: e.target.value})} className="w-full bg-black border border-white/20 focus:border-red-500 outline-none px-4 py-3 text-white text-sm mb-3" />
                        <input required type="email" placeholder="Your email (use your school domain)" value={coach.coach_email} onChange={(e) => setCoach({...coach, coach_email: e.target.value})} className="w-full bg-black border border-white/20 focus:border-red-500 outline-none px-4 py-3 text-white text-sm mb-3" />
                        <input placeholder="Title (e.g. Head Coach)" value={coach.coach_title} onChange={(e) => setCoach({...coach, coach_title: e.target.value})} className="w-full bg-black border border-white/20 focus:border-red-500 outline-none px-4 py-3 text-white text-sm mb-3" />
                        <input required placeholder="School / Program" value={coach.coach_school} onChange={(e) => setCoach({...coach, coach_school: e.target.value})} className="w-full bg-black border border-white/20 focus:border-red-500 outline-none px-4 py-3 text-white text-sm mb-3" />
                        <textarea rows="3" placeholder="Optional note" value={coach.message} onChange={(e) => setCoach({...coach, message: e.target.value})} className="w-full bg-black border border-white/20 focus:border-red-500 outline-none px-4 py-3 text-white text-sm mb-4" />
                        <div className="flex gap-3">
                            <button type="button" onClick={() => setShowCoachVerify(false)} className="flex-1 border border-white/20 text-white/70 py-3 text-xs tracking-[0.3em] uppercase font-display">Cancel</button>
                            <button type="submit" disabled={sending} className="flex-1 bg-red-500 hover:bg-red-600 text-white py-3 text-xs tracking-[0.3em] uppercase font-display disabled:opacity-50">{sending ? "Sending…" : "Submit Verification"}</button>
                        </div>
                    </form>
                </div>
            )}

            <Footer />
        </div>
    );
}

// ================ SCOUT AUTH + SHORTLIST ==============
export function ScoutPortal() {
    const nav = useNavigate();
    const [mode, setMode] = useState("login");  // login | register | shortlist
    const [scout, setScout] = useState(null);
    const [form, setForm] = useState({ name: "", email: "", password: "", organization: "", title: "" });
    const [shortlist, setShortlist] = useState([]);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        const token = localStorage.getItem("fgm_scout_token");
        if (!token) return;
        fetch(`${API}/scouts/me`, { headers: { "X-Scout-Token": token } })
            .then((r) => (r.ok ? r.json() : Promise.reject()))
            .then((me) => { setScout(me); setMode("shortlist"); })
            .catch(() => localStorage.removeItem("fgm_scout_token"));
    }, []);

    useEffect(() => {
        if (mode !== "shortlist") return;
        const token = localStorage.getItem("fgm_scout_token");
        if (!token) return;
        fetch(`${API}/scouts/shortlist`, { headers: { "X-Scout-Token": token } })
            .then((r) => r.json()).then((d) => setShortlist(d.shortlist || [])).catch(() => {});
    }, [mode]);

    async function submit(e) {
        e.preventDefault();
        setBusy(true);
        try {
            const url = mode === "register" ? "/scouts/register" : "/scouts/login";
            const r = await fetch(`${API}${url}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form) });
            const d = await r.json();
            if (!r.ok) throw new Error(d.detail || "Failed");
            localStorage.setItem("fgm_scout_token", d.token);
            setScout({ name: d.name, email: d.email, organization: d.organization });
            setMode("shortlist");
            toast.success(mode === "register" ? "Scout account created" : "Welcome back");
        } catch (err) { toast.error(err.message); } finally { setBusy(false); }
    }

    function logout() {
        localStorage.removeItem("fgm_scout_token");
        setScout(null);
        setMode("login");
    }

    const inputCls = "w-full bg-black border border-white/20 focus:border-red-500 outline-none px-4 py-3 text-white text-sm mb-3";

    return (
        <div className="App grain min-h-screen bg-black text-white" data-testid="scouts-portal">
            <Header />
            <main className="pt-24 pb-24 max-w-4xl mx-auto px-6 md:px-10">
                {mode !== "shortlist" ? (
                    <div className="max-w-md mx-auto py-12">
                        <div className="text-red-500 text-xs tracking-[0.35em] font-display uppercase mb-3">Scout Portal</div>
                        <h1 className="font-display text-4xl uppercase text-white mb-2">{mode === "register" ? "Create Account" : "Sign In"}</h1>
                        <p className="text-white/60 mb-8">Save athletes to your shortlist. Reach out through FGM.</p>
                        <form onSubmit={submit}>
                            {mode === "register" && (
                                <>
                                    <input required placeholder="Your name" data-testid="scout-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className={inputCls} />
                                    <input placeholder="Organization / School" value={form.organization} onChange={(e) => setForm({...form, organization: e.target.value})} className={inputCls} />
                                </>
                            )}
                            <input required type="email" placeholder="Email" data-testid="scout-email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className={inputCls} />
                            <input required type="password" placeholder="Password (min 8 chars)" data-testid="scout-password" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} className={inputCls} />
                            <button type="submit" disabled={busy} data-testid="scout-submit" className="w-full bg-red-500 hover:bg-red-600 text-white py-4 font-display uppercase tracking-[0.3em] text-sm disabled:opacity-50">{busy ? "…" : (mode === "register" ? "Create Account" : "Sign In")}</button>
                        </form>
                        <button onClick={() => setMode(mode === "register" ? "login" : "register")} className="mt-4 text-white/60 hover:text-white text-xs tracking-[0.3em] uppercase font-display">{mode === "register" ? "Already have an account? Sign in" : "New scout? Create account"}</button>
                    </div>
                ) : (
                    <div className="py-12" data-testid="scout-shortlist">
                        <div className="flex items-center justify-between mb-8">
                            <div>
                                <div className="text-red-500 text-xs tracking-[0.35em] font-display uppercase mb-1">Your Shortlist</div>
                                <h1 className="font-display text-3xl uppercase text-white">{scout?.name}</h1>
                                <div className="text-white/50 text-sm">{scout?.organization}</div>
                            </div>
                            <button onClick={logout} className="text-white/60 hover:text-white text-xs tracking-[0.3em] uppercase font-display border border-white/20 px-4 py-2">Sign Out</button>
                        </div>
                        {shortlist.length === 0 ? (
                            <div className="text-center py-16 text-white/50">
                                Your shortlist is empty. <Link to="/athletes" className="text-red-500 border-b border-red-500/40">Browse athletes</Link>
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {shortlist.map((item) => item.athlete && (
                                    <Link key={item.athlete.slug} to={`/athletes/${item.athlete.slug}`} className="border border-white/10 hover:border-red-500 bg-zinc-950 transition-colors flex gap-4 p-4">
                                        <div className="w-20 h-20 bg-zinc-900 overflow-hidden flex-shrink-0">
                                            {item.athlete.photo_url ? <img src={item.athlete.photo_url} alt={item.athlete.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-3xl text-white/20 font-display">{item.athlete.name[0]}</div>}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-white font-display text-lg uppercase truncate">{item.athlete.name}</div>
                                            <div className="text-white/50 text-xs tracking-widest uppercase font-display">{item.athlete.sport} · Class {item.athlete.class_year}</div>
                                            <div className="text-white/40 text-xs mt-1 truncate">{[item.athlete.school, item.athlete.state].filter(Boolean).join(" · ")}</div>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </main>
            <Footer />
        </div>
    );
}
// ================ SUBMISSION FORM ==============
export function AthleteSubmit() {
    const nav = useNavigate();
    const [form, setForm] = useState({
        name: "", email: "", age: "", parent_email: "", parent_consented: false,
        sport: "", position: "", class_year: "", school: "", city: "", state: "",
        height: "", weight: "", gpa: "",
        achievements: "", instagram: "", tiktok: "", twitter: "",
        highlight_url: "", highlight_title: "", photo_url: "",
        contact_public_email: false, content_ownership_confirmed: false, tos_accepted: false,
    });
    const [submitting, setSubmitting] = useState(false);
    const needsParent = form.age && Number(form.age) < 18;

    async function submit(e) {
        e.preventDefault();
        setSubmitting(true);
        try {
            const highlights = form.highlight_url ? [{
                kind: /youtu/.test(form.highlight_url) ? "youtube" : /instagram/.test(form.highlight_url) ? "instagram" : /tiktok/.test(form.highlight_url) ? "tiktok" : /hudl/.test(form.highlight_url) ? "hudl" : "other",
                url: form.highlight_url, title: form.highlight_title || null,
            }] : [];
            const payload = {
                name: form.name, email: form.email, age: Number(form.age),
                parent_email: needsParent ? form.parent_email : null,
                parent_consented: needsParent ? form.parent_consented : false,
                sport: form.sport, position: form.position || null,
                class_year: form.class_year ? Number(form.class_year) : null,
                school: form.school || null, city: form.city || null, state: form.state || null,
                height: form.height || null, weight: form.weight ? Number(form.weight) : null,
                gpa: form.gpa ? Number(form.gpa) : null,
                achievements: form.achievements ? form.achievements.split("\n").filter(Boolean) : [],
                highlights, photo_url: form.photo_url || null,
                instagram: form.instagram || null, tiktok: form.tiktok || null, twitter: form.twitter || null,
                contact_public_email: form.contact_public_email,
                content_ownership_confirmed: form.content_ownership_confirmed,
                tos_accepted: form.tos_accepted,
            };
            const r = await fetch(`${API}/athletes`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
            const d = await r.json();
            if (!r.ok) throw new Error(d.detail || "Submission failed");
            toast.success("Profile submitted — you'll get an email when FGM approves it.");
            nav("/athletes");
        } catch (err) {
            toast.error(err.message);
        } finally {
            setSubmitting(false);
        }
    }

    const inputCls = "w-full bg-black border border-white/20 focus:border-red-500 outline-none px-4 py-3 text-white text-sm font-body";
    const labelCls = "text-white/60 text-[11px] tracking-[0.25em] font-display uppercase mb-1 block";

    return (
        <div className="App grain min-h-screen bg-black text-white" data-testid="athletes-submit">
            <Header />
            <main className="pt-24 pb-24">
                <div className="max-w-3xl mx-auto px-6 md:px-10 py-12">
                    <div className="text-red-500 text-xs tracking-[0.35em] font-display uppercase mb-2">Submit Your Profile</div>
                    <h1 className="font-display text-4xl md:text-5xl uppercase text-white leading-tight">Get Seen.</h1>
                    <p className="mt-4 text-white/60 font-body">Every profile is reviewed before going public. Ages 14-25. FGM does not represent you — we host your highlights.</p>

                    <form onSubmit={submit} className="mt-10 space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div><label className={labelCls}>Name*</label><input required data-testid="submit-name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className={inputCls} /></div>
                            <div><label className={labelCls}>Email*</label><input required type="email" data-testid="submit-email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className={inputCls} /></div>
                            <div><label className={labelCls}>Age* (14-25)</label><input required type="number" min="14" max="25" data-testid="submit-age" value={form.age} onChange={(e) => setForm({...form, age: e.target.value})} className={inputCls} /></div>
                            <div><label className={labelCls}>Sport*</label>
                                <select required data-testid="submit-sport" value={form.sport} onChange={(e) => setForm({...form, sport: e.target.value})} className={inputCls}>
                                    <option value="">Pick one</option>
                                    {SPORTS.map((s) => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                            <div><label className={labelCls}>Position</label><input value={form.position} onChange={(e) => setForm({...form, position: e.target.value})} className={inputCls} /></div>
                            <div><label className={labelCls}>Class Year</label>
                                <select value={form.class_year} onChange={(e) => setForm({...form, class_year: e.target.value})} className={inputCls}>
                                    <option value="">—</option>
                                    {[2025,2026,2027,2028,2029,2030,2031].map((y) => <option key={y} value={y}>{y}</option>)}
                                </select>
                            </div>
                            <div><label className={labelCls}>School</label><input value={form.school} onChange={(e) => setForm({...form, school: e.target.value})} className={inputCls} /></div>
                            <div><label className={labelCls}>City</label><input value={form.city} onChange={(e) => setForm({...form, city: e.target.value})} className={inputCls} /></div>
                            <div><label className={labelCls}>State</label>
                                <select value={form.state} onChange={(e) => setForm({...form, state: e.target.value})} className={inputCls}>
                                    <option value="">—</option>
                                    {STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                            <div><label className={labelCls}>Height</label><input placeholder="6'1" value={form.height} onChange={(e) => setForm({...form, height: e.target.value})} className={inputCls} /></div>
                            <div><label className={labelCls}>Weight (lb)</label><input type="number" value={form.weight} onChange={(e) => setForm({...form, weight: e.target.value})} className={inputCls} /></div>
                            <div><label className={labelCls}>GPA</label><input type="number" step="0.1" min="0" max="5" value={form.gpa} onChange={(e) => setForm({...form, gpa: e.target.value})} className={inputCls} /></div>
                        </div>

                        {needsParent && (
                            <div className="border border-red-500/40 bg-red-500/5 p-4">
                                <div className="text-red-500 text-xs tracking-[0.35em] font-display uppercase mb-2">Parental Consent Required (Under 18)</div>
                                <div className="space-y-3">
                                    <div><label className={labelCls}>Parent/Guardian Email*</label><input required={needsParent} type="email" data-testid="submit-parent-email" value={form.parent_email} onChange={(e) => setForm({...form, parent_email: e.target.value})} className={inputCls} /></div>
                                    <label className="flex items-start gap-3 text-sm text-white/80"><input required={needsParent} type="checkbox" data-testid="submit-parent-consent" checked={form.parent_consented} onChange={(e) => setForm({...form, parent_consented: e.target.checked})} className="mt-1" /><span>My parent/guardian has reviewed this profile and consents to it being published on FGM Apparel.</span></label>
                                </div>
                            </div>
                        )}

                        <div><label className={labelCls}>Achievements (one per line)</label><textarea rows="4" placeholder="State Champion 2025&#10;40yd: 4.53" value={form.achievements} onChange={(e) => setForm({...form, achievements: e.target.value})} className={inputCls} /></div>

                        <div><label className={labelCls}>Highlight Video URL (YouTube / IG / TikTok / Hudl)</label><input value={form.highlight_url} onChange={(e) => setForm({...form, highlight_url: e.target.value})} className={inputCls} /></div>
                        <div><label className={labelCls}>Highlight Title</label><input value={form.highlight_title} onChange={(e) => setForm({...form, highlight_title: e.target.value})} className={inputCls} /></div>
                        <div><label className={labelCls}>Photo URL (headshot / action shot)</label><input value={form.photo_url} onChange={(e) => setForm({...form, photo_url: e.target.value})} className={inputCls} /></div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div><label className={labelCls}>Instagram</label><input placeholder="@handle" value={form.instagram} onChange={(e) => setForm({...form, instagram: e.target.value})} className={inputCls} /></div>
                            <div><label className={labelCls}>TikTok</label><input placeholder="@handle" value={form.tiktok} onChange={(e) => setForm({...form, tiktok: e.target.value})} className={inputCls} /></div>
                            <div><label className={labelCls}>Twitter/X</label><input placeholder="@handle" value={form.twitter} onChange={(e) => setForm({...form, twitter: e.target.value})} className={inputCls} /></div>
                        </div>

                        <div className="border-t border-white/10 pt-6 space-y-3">
                            <label className="flex items-start gap-3 text-sm text-white/80"><input type="checkbox" data-testid="submit-public-email" checked={form.contact_public_email} onChange={(e) => setForm({...form, contact_public_email: e.target.checked})} className="mt-1" /><span>Show my email publicly (otherwise scouts contact you through a gated form)</span></label>
                            <label className="flex items-start gap-3 text-sm text-white/80"><input required type="checkbox" data-testid="submit-ownership" checked={form.content_ownership_confirmed} onChange={(e) => setForm({...form, content_ownership_confirmed: e.target.checked})} className="mt-1" /><span>I own or have permission to publish every photo, video, and stat submitted here.*</span></label>
                            <label className="flex items-start gap-3 text-sm text-white/80"><input required type="checkbox" data-testid="submit-tos" checked={form.tos_accepted} onChange={(e) => setForm({...form, tos_accepted: e.target.checked})} className="mt-1" /><span>I accept the FGM Apparel Terms of Service and Privacy Policy.*</span></label>
                        </div>

                        <button type="submit" disabled={submitting} data-testid="submit-submit" className="w-full bg-red-500 hover:bg-red-600 text-white py-4 font-display uppercase tracking-[0.3em] text-sm disabled:opacity-50">{submitting ? "Submitting…" : "Submit For Review →"}</button>
                    </form>
                </div>
            </main>
            <Footer />
        </div>
    );
}
